import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CareShell } from "@/components/CareShell";
import { HealthcareCard } from "@/components/care/HealthcareCard";
import { toast } from "sonner";
import { CalendarPlus, Phone, Wallet, HeartHandshake, ChevronRight, CalendarClock, ShieldCheck, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function CareHome() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState([]);
    const [visits, setVisits] = useState(null);
    const [manager, setManager] = useState(null);

    useEffect(() => {
        api.get("/care/plans").then((r) => setPlans(r.data.subscriptions || [])).catch(() => {});
        api.get("/care/visits").then((r) => setVisits(r.data)).catch(() => setVisits([]));
        api.get("/care/manager").then((r) => setManager(r.data)).catch(() => {});
    }, []);

    const callManager = async () => {
        try { await api.post("/care/manager-request"); toast.success("Your Care Manager will reach out shortly"); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        if (manager?.phone) window.location.href = `tel:${manager.phone}`;
    };

    const upcoming = (visits || []).find((v) => v.status !== "CANCELLED" && v.status !== "COMPLETED");

    return (
        <CareShell title="HOMIIE Care">
            <div className="px-5 pt-2">
                <div className="relative overflow-hidden rounded-3xl" style={{ boxShadow: "0 20px 40px -10px rgba(16,42,67,0.1)" }}>
                    <img src={HERO} alt="HOMIIE Care" className="h-44 w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#102A43]/70 to-transparent" />
                    <div className="absolute bottom-0 p-5 text-white">
                        <p className="text-xs font-semibold text-white/80">Good day, {user?.name?.split(" ")[0] || "there"}</p>
                        <h1 className="font-heading text-2xl font-extrabold">Trusted care, at home.</h1>
                        <p className="mt-0.5 text-xs text-white/85">Verified caregivers · transparent · family-first</p>
                    </div>
                </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3 px-5">
                <QuickAction testid="care-action-book" icon={CalendarPlus} label="Book a Visit" onClick={() => nav("/care/plans")} />
                <QuickAction testid="care-action-manager" icon={Phone} label="Care Manager" onClick={callManager} />
                <QuickAction testid="care-action-wallet" icon={Wallet} label="Wallet" onClick={() => nav("/wallet")} />
            </div>

            <HealthcareCard />

            {upcoming && (
                <div className="mt-5 px-5">
                    <button onClick={() => nav("/care/bookings")} data-testid="care-upcoming" className="w-full rounded-3xl bg-[#FFF3E8] p-5 text-left active:scale-[0.98]" style={{ boxShadow: "0 10px 25px -5px rgba(244,63,94,0.08)" }}>
                        <div className="flex items-center gap-2 text-[#F43F5E]"><CalendarClock size={16} /><span className="text-xs font-bold uppercase tracking-wide">Upcoming care visit</span></div>
                        <h3 className="mt-1 font-heading text-lg font-extrabold text-navy">{upcoming.plan_name}</h3>
                        <p className="text-sm text-navy/60">For {upcoming.member_name} · {upcoming.scheduled_date || upcoming.date} · {upcoming.scheduled_window || upcoming.window}</p>
                        {upcoming.caregiver_name && <p className="text-sm font-semibold text-navy">Caregiver: {upcoming.caregiver_name}</p>}
                        <span className="mt-2 inline-block rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[#F43F5E]">{upcoming.status}</span>
                    </button>
                </div>
            )}

            <div className="mt-6 px-5">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="font-heading text-lg font-extrabold text-navy">Care Plans</h2>
                    <button data-testid="care-see-plans" onClick={() => nav("/care/plans")} className="flex items-center gap-0.5 text-xs font-bold text-[#F43F5E]">Explore all <ChevronRight size={14} /></button>
                </div>
                {visits === null && <Loader2 className="h-6 w-6 animate-spin text-[#F43F5E]" />}
                <div className="space-y-3">
                    {plans.slice(0, 3).map((p) => (
                        <button key={p.id} data-testid={`care-plan-${p.id}`} onClick={() => nav("/care/plans")} className="flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left" style={{ boxShadow: "0 8px 20px -8px rgba(16,42,67,0.08)" }}>
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF3E8] text-[#F43F5E]"><HeartHandshake size={20} /></div>
                            <div className="flex-1">
                                <h3 className="font-heading text-sm font-extrabold text-navy">{p.name}</h3>
                                <p className="text-xs text-navy/55">{p.tagline} · {p.duration}</p>
                            </div>
                            <span className="font-heading text-sm font-extrabold text-[#F43F5E]">{p.price_label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6 px-5">
                <div className="flex items-start gap-2.5 rounded-2xl bg-white p-4" style={{ boxShadow: "0 8px 20px -8px rgba(16,42,67,0.06)" }}>
                    <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-500" />
                    <div>
                        <h3 className="font-heading text-sm font-bold text-navy">Your family, protected</h3>
                        <p className="text-xs text-navy/55">Background-verified caregivers, consent-gated health records and a dedicated Care Manager. HOMIIE Care does not provide medical advice or guarantee medical outcomes.</p>
                    </div>
                </div>
            </div>
        </CareShell>
    );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4 active:scale-95" style={{ boxShadow: "0 8px 20px -8px rgba(16,42,67,0.08)" }}>
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F43F5E] text-white"><Icon size={20} /></span>
            <span className="text-center text-[11px] font-bold text-navy">{label}</span>
        </button>
    );
}
